import { useState } from 'react'
import { motion } from 'framer-motion'
import type { Message } from '@/types/models'
import { toggleMessageLike, buildBurstImageReplySnapshot } from '@/lib/chatData'
import { ImageContextMenu } from '@/components/chat/ImageContextMenu'
import { ChatImageWithLoadingState } from '@/components/chat/ChatImageWithLoadingState'
import { useLongPress } from '@/hooks/useLongPress'
import { useDeferredImageTapOpen } from '@/hooks/useDeferredImageTapOpen'
import { useChatImagePreview } from '@/components/chat/ChatImagePreviewContext'
import {
  getFlatImageUrlsFromMessage,
  getMessageBubbleCornerClassName,
} from '@/lib/chatImageBurst'
import { cn } from '@/lib/utils'
import { ChatMessageSwipeReplyRow } from '@/components/chat/ChatMessageSwipeReplyRow'

type ImageTileDescriptor = {
  url: string
  messageId: string
  messageWasLikedByRecipient: boolean
}

function flattenBurstMessagesToOrderedImageTiles(messages: Message[]): ImageTileDescriptor[] {
  const orderedTiles: ImageTileDescriptor[] = []
  for (const message of messages) {
    for (const url of getFlatImageUrlsFromMessage(message)) {
      orderedTiles.push({
        url,
        messageId: message.id,
        messageWasLikedByRecipient: Boolean(message.isLiked),
      })
    }
  }
  return orderedTiles
}

interface ChatImageBurstMessageProps {
  messages: Message[]
  isOwnMessage: boolean
  isContinuation: boolean
  isLastInGroup: boolean
  isLastInChat: boolean
  senderName?: string
  senderAvatar?: string
  senderFallbackInitial?: string
  chatId: string
  artistId?: string
  onBookingCreated?: () => void
  isArtist?: boolean
  onRetryMessage?: (clientGeneratedId: string) => void
  onAddToTimeline?: (imageUrl: string) => void
  onReplyToImageBurst?: (messages: Message[]) => void
}

export function ChatImageBurstMessage({
  messages,
  isOwnMessage,
  isContinuation,
  isLastInGroup,
  isLastInChat,
  senderName,
  senderAvatar,
  senderFallbackInitial,
  chatId,
  artistId: _artistId,
  onBookingCreated: _onBookingCreated,
  isArtist = false,
  onRetryMessage,
  onAddToTimeline,
  onReplyToImageBurst,
}: ChatImageBurstMessageProps) {
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; imageUrl: string } | null>(
    null
  )

  const imageTilesOrdered = flattenBurstMessagesToOrderedImageTiles(messages)
  const flatPreviewUrlList = imageTilesOrdered.map(tile => tile.url)
  const totalImageCountInBurst = flatPreviewUrlList.length
  const exceedsFourThumbnailCap = totalImageCountInBurst > 4
  const hiddenImagesCountBeyondFourthSlot = exceedsFourThumbnailCap ? totalImageCountInBurst - 4 : 0

  const bubbleCornerClassName = getMessageBubbleCornerClassName(isContinuation, isOwnMessage)

  const anyMessageInBurstWasLiked = imageTilesOrdered.some(tile => tile.messageWasLikedByRecipient)
  const shouldReserveSpaceBelowBurstForHeartPillOverhang = anyMessageInBurstWasLiked && !isLastInGroup

  const containerHorizontalAlignmentClassName = isOwnMessage ? 'justify-end' : 'justify-start'

  const lastBurstMessageRecord = messages[messages.length - 1]

  const handleDoubleClickToggleLikeForMessage = async (_event: React.MouseEvent, messageIdentifier: string) => {
    if (isOwnMessage) return
    try {
      await toggleMessageLike(chatId, messageIdentifier)
    } catch (error) {
      console.error('Error toggling message like:', error)
    }
  }

  const handleLongPressOpenTimelineMenu = (event: React.TouchEvent | React.MouseEvent, imageUrl: string) => {
    if (isArtist && !isOwnMessage && onAddToTimeline) {
      event.preventDefault()
      const rect = (event.target as HTMLElement).getBoundingClientRect()
      setContextMenu({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
        imageUrl,
      })
    }
  }

  const artistCanLongPressToShareReferenceToTimeline =
    isArtist && !isOwnMessage && !!onAddToTimeline

  const swipeReplyToBurstGestureIsAvailable =
    !isOwnMessage &&
    Boolean(onReplyToImageBurst) &&
    buildBurstImageReplySnapshot(messages) !== null

  return (
    <>
      <ChatMessageSwipeReplyRow
        swipeReplyEnabled={swipeReplyToBurstGestureIsAvailable}
        onSwipeConfirmReply={() => onReplyToImageBurst?.(messages)}
      >
      <div
        id={messages[0]?.id ? `chat-message-${messages[0].id}` : undefined}
        className={cn(
          'flex w-full',
          containerHorizontalAlignmentClassName,
          shouldReserveSpaceBelowBurstForHeartPillOverhang ? 'mb-[calc(0.5rem+8px)]' : 'mb-2'
        )}
      >
        <div className={`flex w-full items-end gap-2 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
          {!isOwnMessage && (
            <div className="flex h-8 w-8 flex-shrink-0">
              {isLastInGroup &&
                (senderAvatar ? (
                  <div className="h-8 w-8 overflow-hidden rounded-full bg-muted">
                    <img src={senderAvatar} alt={senderName || 'Avatar'} className="h-full w-full object-cover" />
                  </div>
                ) : (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                    <span className="text-xs font-semibold text-foreground">
                      {(senderFallbackInitial || '?').toUpperCase()}
                    </span>
                  </div>
                ))}
            </div>
          )}

          <div className={`group flex min-w-0 max-w-[70%] flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}>
            <motion.div
              data-chat-scroll-anchor-bubble
              animate={{ scale: 1, y: 0, opacity: 1 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className={cn(
                'relative cursor-pointer select-none bg-transparent p-0 shadow-sm',
                bubbleCornerClassName,
                'overflow-visible',
                !isOwnMessage && 'transition-shadow hover:shadow-md'
              )}
            >
              <div className={cn('relative z-[1] overflow-hidden', bubbleCornerClassName)}>
                {totalImageCountInBurst === 2 && (
                  <div className="grid max-w-[260px] grid-cols-2 gap-0.5">
                    {imageTilesOrdered.slice(0, 2).map((descriptor, sliceIndexWithinBurst) => (
                      <ChatImageBurstGridTile
                        key={`${descriptor.url}-${descriptor.messageId}-${sliceIndexWithinBurst}`}
                        descriptor={descriptor}
                        previewUrlsInOrder={flatPreviewUrlList}
                        flatPreviewStartIndex={sliceIndexWithinBurst}
                        isOwnBurstMessage={isOwnMessage}
                        onDoubleClickToggleLike={handleDoubleClickToggleLikeForMessage}
                        mergeLongPressForTimelineStopPropagation={
                          artistCanLongPressToShareReferenceToTimeline
                        }
                        onLongPressTimelineForImageUrl={(event, imageUrlFromTile) =>
                          handleLongPressOpenTimelineMenu(event, imageUrlFromTile)
                        }
                        additionalHiddenImagesCountBadge={0}
                      />
                    ))}
                  </div>
                )}
                {totalImageCountInBurst === 3 && (
                  <div className="grid aspect-square max-w-[260px] grid-cols-2 grid-rows-2 gap-0.5">
                    <ChatImageBurstGridTile
                      key={`${imageTilesOrdered[0].url}-0`}
                      descriptor={imageTilesOrdered[0]}
                      previewUrlsInOrder={flatPreviewUrlList}
                      flatPreviewStartIndex={0}
                      isOwnBurstMessage={isOwnMessage}
                      onDoubleClickToggleLike={handleDoubleClickToggleLikeForMessage}
                      mergeLongPressForTimelineStopPropagation={
                        artistCanLongPressToShareReferenceToTimeline
                      }
                      onLongPressTimelineForImageUrl={(event, imageUrlFromTile) =>
                        handleLongPressOpenTimelineMenu(event, imageUrlFromTile)
                      }
                      shellGridClassName="row-span-2 min-h-0"
                      additionalHiddenImagesCountBadge={0}
                    />
                    <ChatImageBurstGridTile
                      key={`${imageTilesOrdered[1].url}-1`}
                      descriptor={imageTilesOrdered[1]}
                      previewUrlsInOrder={flatPreviewUrlList}
                      flatPreviewStartIndex={1}
                      isOwnBurstMessage={isOwnMessage}
                      onDoubleClickToggleLike={handleDoubleClickToggleLikeForMessage}
                      mergeLongPressForTimelineStopPropagation={
                        artistCanLongPressToShareReferenceToTimeline
                      }
                      onLongPressTimelineForImageUrl={(event, imageUrlFromTile) =>
                        handleLongPressOpenTimelineMenu(event, imageUrlFromTile)
                      }
                      shellGridClassName="min-h-0"
                      additionalHiddenImagesCountBadge={0}
                    />
                    <ChatImageBurstGridTile
                      key={`${imageTilesOrdered[2].url}-2`}
                      descriptor={imageTilesOrdered[2]}
                      previewUrlsInOrder={flatPreviewUrlList}
                      flatPreviewStartIndex={2}
                      isOwnBurstMessage={isOwnMessage}
                      onDoubleClickToggleLike={handleDoubleClickToggleLikeForMessage}
                      mergeLongPressForTimelineStopPropagation={
                        artistCanLongPressToShareReferenceToTimeline
                      }
                      onLongPressTimelineForImageUrl={(event, imageUrlFromTile) =>
                        handleLongPressOpenTimelineMenu(event, imageUrlFromTile)
                      }
                      shellGridClassName="min-h-0"
                      additionalHiddenImagesCountBadge={0}
                    />
                  </div>
                )}
                {totalImageCountInBurst >= 4 && (
                    <div className="grid aspect-square max-w-[260px] grid-cols-2 grid-rows-2 gap-0.5">
                      {imageTilesOrdered.slice(0, 3).map((descriptor, sliceIndexWithinBurst) => (
                        <ChatImageBurstGridTile
                          key={`${descriptor.url}-${descriptor.messageId}-${sliceIndexWithinBurst}`}
                          descriptor={descriptor}
                          previewUrlsInOrder={flatPreviewUrlList}
                          flatPreviewStartIndex={sliceIndexWithinBurst}
                          isOwnBurstMessage={isOwnMessage}
                          onDoubleClickToggleLike={handleDoubleClickToggleLikeForMessage}
                          mergeLongPressForTimelineStopPropagation={
                            artistCanLongPressToShareReferenceToTimeline
                          }
                          onLongPressTimelineForImageUrl={(event, imageUrlFromTile) =>
                            handleLongPressOpenTimelineMenu(event, imageUrlFromTile)
                          }
                          shellGridClassName="min-h-0"
                          additionalHiddenImagesCountBadge={0}
                        />
                      ))}
                      <ChatImageBurstGridTile
                        key={`${imageTilesOrdered[3].url}-fourth`}
                        descriptor={imageTilesOrdered[3]}
                        previewUrlsInOrder={flatPreviewUrlList}
                        flatPreviewStartIndex={3}
                        isOwnBurstMessage={isOwnMessage}
                        onDoubleClickToggleLike={handleDoubleClickToggleLikeForMessage}
                        mergeLongPressForTimelineStopPropagation={
                          artistCanLongPressToShareReferenceToTimeline
                        }
                        onLongPressTimelineForImageUrl={(event, imageUrlFromTile) =>
                          handleLongPressOpenTimelineMenu(event, imageUrlFromTile)
                        }
                        shellGridClassName="min-h-0"
                        additionalHiddenImagesCountBadge={hiddenImagesCountBeyondFourthSlot}
                      />
                    </div>
                  )}
                <div className="pointer-events-none absolute inset-0 z-[2] bg-primary/5" />
              </div>

              {anyMessageInBurstWasLiked && (
                <div
                  className="pointer-events-none absolute bottom-0 left-[6px] z-[5] flex translate-y-1/2 items-center justify-center"
                  aria-hidden
                >
                  <div className="rounded-full border-2 border-background bg-muted px-[6px] py-[3px]">
                    <span className="text-[11px] font-medium leading-none">❤️</span>
                  </div>
                </div>
              )}
            </motion.div>

            {isOwnMessage && isLastInChat && lastBurstMessageRecord?.status && (
              <div className="mb-1 mt-1.5 flex items-center justify-end text-xs opacity-60">
                {lastBurstMessageRecord.status === 'sending' && (
                  <span className="text-[10px] italic">Sending...</span>
                )}
                {lastBurstMessageRecord.status === 'sent' && !lastBurstMessageRecord.isRead && (
                  <span className="text-[10px] italic">Sent</span>
                )}
                {lastBurstMessageRecord.isRead && <span className="text-[10px] italic">Seen</span>}
                {lastBurstMessageRecord.status === 'failed' && (
                  <div className="flex items-center gap-1.5 text-red-400">
                    <span className="text-[10px] italic">Failed to send</span>
                    {lastBurstMessageRecord.clientGeneratedId && onRetryMessage && (
                      <button
                        type="button"
                        onClick={event => {
                          event.stopPropagation()
                          onRetryMessage(lastBurstMessageRecord.clientGeneratedId!)
                        }}
                        className="ml-1 text-[10px] text-red-400 underline hover:text-red-500"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      </ChatMessageSwipeReplyRow>

      {contextMenu && onAddToTimeline && (
        <ImageContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onAddToTimeline={() => onAddToTimeline(contextMenu.imageUrl)}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  )
}

function ChatImageBurstGridTile({
  descriptor,
  previewUrlsInOrder,
  flatPreviewStartIndex,
  isOwnBurstMessage,
  onDoubleClickToggleLike,
  mergeLongPressForTimelineStopPropagation,
  onLongPressTimelineForImageUrl,
  shellGridClassName = 'aspect-square min-h-0',
  additionalHiddenImagesCountBadge,
}: {
  descriptor: ImageTileDescriptor
  previewUrlsInOrder: string[]
  flatPreviewStartIndex: number
  isOwnBurstMessage: boolean
  onDoubleClickToggleLike: (event: React.MouseEvent, messageId: string) => void | Promise<void>
  mergeLongPressForTimelineStopPropagation: boolean
  onLongPressTimelineForImageUrl: (event: React.TouchEvent | React.MouseEvent, imageUrl: string) => void
  shellGridClassName?: string
  additionalHiddenImagesCountBadge: number
}) {
  const { openImagePreview } = useChatImagePreview()

  const { scheduleDeferredOpen, cancelDeferredOpen } = useDeferredImageTapOpen(() =>
    openImagePreview({
      urls: previewUrlsInOrder,
      initialIndex: flatPreviewStartIndex,
    })
  )

  const baseLongPressBinding = useLongPress({
    onLongPress: event => onLongPressTimelineForImageUrl(event, descriptor.url),
    onClick: scheduleDeferredOpen,
    ms: 500,
  })

  const longPressPropsAfterOptionalStopPropagation =
    mergeLongPressForTimelineStopPropagation
      ? {
          ...baseLongPressBinding,
          onTouchStart: (event: React.TouchEvent) => {
            baseLongPressBinding.onTouchStart(event)
            event.stopPropagation()
          },
          onMouseDown: (event: React.MouseEvent) => {
            baseLongPressBinding.onMouseDown(event)
            event.stopPropagation()
          },
        }
      : baseLongPressBinding

  return (
    <div
      className={cn('relative overflow-hidden bg-black/5', shellGridClassName)}
      {...longPressPropsAfterOptionalStopPropagation}
      onDoubleClick={event => {
        cancelDeferredOpen()
        event.preventDefault()
        if (!isOwnBurstMessage) void onDoubleClickToggleLike(event, descriptor.messageId)
      }}
    >
      <ChatImageWithLoadingState
        src={descriptor.url}
        alt=""
        className="h-full w-full rounded-none object-cover"
      />
      {additionalHiddenImagesCountBadge > 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/50 text-lg font-semibold text-white backdrop-blur-[1px]">
          +{additionalHiddenImagesCountBadge}
        </div>
      )}
    </div>
  )
}
